interface ImportMetaWithEnv extends ImportMeta {
  readonly env: {
    readonly VITE_REDIRECT_URI?: string;
  };
}

import { resolveBundleOrigin } from '../config/appOrigin';
import type { ScheduledMeeting } from '../contexts';
import { isHostedByTeamHub } from '../services/hostAuthService';

/**
 * Returns the base URL for meeting links.
 *
 * In hosted (Module Federation / TeamHub) mode the app runs on the host's
 * origin, so `window.location.origin` would produce links pointing at TeamHub
 * instead of the NG Meet deployment. We therefore derive the base from the
 * origin the bundle was served from (`develop.`/`staging.`/`meetings.sangoma.com`).
 *
 * SM-1167 / SM-1243: `resolveBundleOrigin()` is the strict variant that returns
 * `null` instead of falling back to the window origin, so an unresolvable bundle
 * origin still lets VITE_REDIRECT_URI take precedence below.
 *
 * In standalone mode it prioritizes the VITE_REDIRECT_URI environment variable,
 * falls back to the current window origin, and finally to a default localhost.
 */
export const getBaseUrl = (): string => {
  if (isHostedByTeamHub()) {
    const federatedOrigin = resolveBundleOrigin();
    if (federatedOrigin) {
      return federatedOrigin;
    }
  }

  const env = (import.meta as ImportMetaWithEnv).env;
  const baseUrl = env?.VITE_REDIRECT_URI;
  if (baseUrl) {
    return baseUrl;
  }

  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  return 'http://localhost:8081';
};

/**
 * Build meeting path (without origin) with optional password fragment.
 */
export function buildMeetingPath(room: string, password?: string | null): string {
  const cleanPassword = password?.replace('#', '').trim();
  return `/${room}${cleanPassword ? `#${cleanPassword}` : ''}`;
}

/**
 * Build full meeting URL with optional password fragment
 */
export const buildMeetingUrl = (base: string, meeting: ScheduledMeeting): string => {
  return `${base.replace(/\/$/, '')}${buildMeetingPath(meeting.room, meeting.password)}`;
};

/**
 * Get the URL for the dial-in numbers page (more phone numbers)
 * @param room - The meeting room identifier
 * @returns The full URL to the dial-in numbers page
 */
export const getDialInNumbersUrl = (room: string): string => {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/dids?room=${room}`;
};
